import { useState } from "react";
import { Alert, Button, Form, Input, App as AntApp } from "antd";
import { useLocation, useNavigate } from "react-router-dom";

import { authApi } from "../api/auth";
import { useAuthStore } from "../store/auth";

export function Login() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();
  const location = useLocation();
  const { notification } = AntApp.useApp();

  const onFinish = async (values: { identifier: string; password: string }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await authApi.login(values.identifier.trim(), values.password);
      setAuth({
        user: res.user,
        access: res.access,
        refresh: res.refresh,
        must_change_password: res.must_change_password,
      });
      if (res.must_change_password) {
        navigate("/change-password", { replace: true });
      } else {
        const fallback = res.user.role === "superuser" ? "/admins" : "/dashboard";
        const stateFrom = (location.state as { from?: string } | null)?.from;
        const to = stateFrom && stateFrom !== "/login" && stateFrom !== "/" ? stateFrom : fallback;
        navigate(to, { replace: true });
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string; non_field_errors?: string[] } } };
      const detail =
        err.response?.data?.non_field_errors?.[0] ||
        err.response?.data?.detail ||
        "Invalid credentials";
      setError(detail);
      notification.error({ message: detail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="split-auth">
      <div className="split-auth-left">
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 32, fontWeight: 600 }}>ERPMan</div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 8 }}>
            Order Management System
          </div>
          <svg
            width="260"
            height="200"
            viewBox="0 0 260 200"
            style={{ marginTop: 40, stroke: "#CBD5E1", fill: "none", strokeWidth: 1.2 }}
          >
            <rect x="40" y="20" width="180" height="160" rx="4" />
            <line x1="60" y1="50" x2="200" y2="50" />
            <line x1="60" y1="70" x2="160" y2="70" />
            <line x1="60" y1="90" x2="200" y2="90" />
            <line x1="60" y1="110" x2="140" y2="110" />
            <line x1="60" y1="130" x2="200" y2="130" />
            <line x1="60" y1="150" x2="180" y2="150" />
            <line x1="150" y1="160" x2="200" y2="160" />
          </svg>
        </div>
        <div style={{ position: "absolute", bottom: 24, left: 24, fontSize: 11, color: "var(--text-muted)" }}>
          Made by Reflexlabs · v1.0.0
        </div>
      </div>
      <div className="split-auth-right">
        <div className="auth-card">
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Sign in to your account</h1>
          <div style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 4 }}>
            Employees sign in with their Employee ID. Admins use email.
          </div>
          {error && (
            <Alert type="error" showIcon message={error} style={{ marginTop: 16 }} />
          )}
          <Form layout="vertical" onFinish={onFinish} style={{ marginTop: 24 }} requiredMark={false}>
            <Form.Item
              label="Email or Employee ID"
              name="identifier"
              rules={[{ required: true, message: "Enter your email or Employee ID" }]}
            >
              <Input placeholder="you@company.com or EMP00001" size="large" autoComplete="username" />
            </Form.Item>
            <Form.Item label="Password" name="password" rules={[{ required: true }]}>
              <Input.Password placeholder="••••••••" size="large" />
            </Form.Item>
            <Button type="primary" htmlType="submit" size="large" block loading={loading}>
              Sign In
            </Button>
          </Form>
          <div style={{ textAlign: "center", marginTop: 24, fontSize: 13, color: "var(--text-secondary)" }}>
            Forgot password? Contact your admin.
          </div>
        </div>
      </div>
    </div>
  );
}
